import React, { createContext, useContext, useEffect, useState } from 'react';
import { injectAuthToken } from '../../api/apiClient';

// Development Auth0 Provider that bypasses secure origin checks
const Auth0Context = createContext();

export const useAuth0 = () => {
  const context = useContext(Auth0Context);
  if (!context) {
    throw new Error('useAuth0 must be used within an Auth0Provider');
  }
  return context;
};

export const DevAuth0Provider = ({ children, domain, clientId, authorizationParams, onRedirectCallback }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [auth0Client, setAuth0Client] = useState(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return; // Prevent multiple initializations

    // Browser close detection is handled in a separate useEffect to avoid dependency issues

    const initAuth = async () => {
      try {
        // console.log('Initializing real Auth0 for:', window.location.origin);
        
        // Firefox-specific initialization delay to handle localStorage timing issues
        const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
        if (isFirefox) {
          // console.log('Firefox detected, applying compatibility workarounds');
          // Small delay to ensure localStorage is fully available
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        setInitialized(true);

        // Clear any stale auth state on initialization
        const clearStaleAuth = () => {
          const storedToken = localStorage.getItem('auth0_access_token');
          const storedUser = localStorage.getItem('auth0_user');

          // If we have partial auth data but no valid user, clear everything
          if (storedToken && !storedUser) {
            // console.log('Clearing stale auth data');
            localStorage.removeItem('auth0_access_token');
            localStorage.removeItem('auth0_id_token');
            localStorage.removeItem('auth0_user');
            localStorage.removeItem('auth0_login_time');
            localStorage.removeItem('auth0_state');
            localStorage.removeItem('auth0_nonce');
          }
        };

        clearStaleAuth();

        // Check if we're handling a callback from Auth0
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        // Check both localStorage and sessionStorage for state (Firefox compatibility)
        const storedState = localStorage.getItem('auth0_state') || sessionStorage.getItem('auth0_state');

        // Handle Auth0 errors (like organization membership requirement)
        if (error) {
          handleAuth0Error(error, errorDescription);
          setIsLoading(false);
          return;
        }

        if (code && state && state === storedState) {
          console.log('Auth: Exchanging code for tokens (backend may take a moment)...');
          const AUTH_EXCHANGE_TIMEOUT_MS = 20000; // 20s – avoid infinite loading if backend is down
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), AUTH_EXCHANGE_TIMEOUT_MS);
          try {
            // Exchange code for tokens via backend (with timeout so we never hang)
            const tokenResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth0/exchange-code`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                code: code,
                redirect_uri: window.location.origin,
              }),
              signal: controller.signal,
            });
            clearTimeout(timeoutId);

            if (tokenResponse.ok) {
              const result = await tokenResponse.json();
              // console.log('Token exchange successful:', { user: result.user?.email, hasToken: !!result.access_token });

              // Store tokens and user data
              localStorage.setItem('auth0_access_token', result.access_token);
              localStorage.setItem('auth0_id_token', result.id_token);
              localStorage.setItem('auth0_user', JSON.stringify(result.user));
              localStorage.setItem('auth0_login_time', Date.now().toString()); // Store login time for session duration

              setUser(result.user);
              setIsAuthenticated(true);

              // Clean up URL and storage
              window.history.replaceState({}, document.title, window.location.pathname);
              localStorage.removeItem('auth0_state');
              localStorage.removeItem('auth0_nonce');
              sessionStorage.removeItem('auth0_state');
              sessionStorage.removeItem('auth0_nonce');

              // console.log('Real Auth0 login successful:', result.user.email);
            } else {
              let errorMessage = 'Login failed. Please try again.';
              try {
                const errorBody = await tokenResponse.json();
                errorMessage = errorBody.message || errorMessage;
                console.error('Token exchange failed:', { status: tokenResponse.status, error: errorBody });
              } catch (_) {
                console.error('Token exchange failed:', tokenResponse.status, tokenResponse.statusText);
              }
              
              // Clear all Auth0 state on failure to prevent retry loops
              localStorage.removeItem('auth0_access_token');
              localStorage.removeItem('auth0_id_token');
              localStorage.removeItem('auth0_user');
              localStorage.removeItem('auth0_login_time');
              localStorage.removeItem('auth0_state');
              localStorage.removeItem('auth0_nonce');
              sessionStorage.removeItem('auth0_state');
              sessionStorage.removeItem('auth0_nonce');
              
              // Clear URL parameters
              window.history.replaceState({}, document.title, window.location.pathname);
              
              setError(new Error(errorMessage));
              setIsAuthenticated(false);
              setUser(null);
              setIsLoading(false);
              return;
            }
          } catch (err) {
            clearTimeout(timeoutId);
            const isAbort = err.name === 'AbortError';
            console.error(isAbort ? 'Token exchange timed out (backend may be unavailable).' : 'Token exchange error:', err);
            
            // Clear all Auth0 state on failure to prevent retry loops
            localStorage.removeItem('auth0_access_token');
            localStorage.removeItem('auth0_id_token');
            localStorage.removeItem('auth0_user');
            localStorage.removeItem('auth0_login_time');
            localStorage.removeItem('auth0_state');
            localStorage.removeItem('auth0_nonce');
            sessionStorage.removeItem('auth0_state');
            sessionStorage.removeItem('auth0_nonce');
            
            // Clear URL parameters so user can try again
            window.history.replaceState({}, document.title, window.location.pathname);
            
            setError(isAbort 
              ? new Error('Login request timed out. The server may be unavailable. Please try again.') 
              : err);
            setIsAuthenticated(false);
            setUser(null);
            setIsLoading(false);
            return;
          }
        } else {
          // Check if we have stored tokens and validate them
          const storedToken = localStorage.getItem('auth0_access_token');
          const storedUser = localStorage.getItem('auth0_user');
          // console.log('Checking stored session:', { hasToken: !!storedToken, hasUser: !!storedUser });

          if (storedToken && storedUser) {
            try {
              // Only validate token expiration, don't clear session during initialization
              const userData = JSON.parse(storedUser);
              
              // Check if token is expired
              if (isTokenExpired && isTokenExpired(storedToken)) {
                // console.log('Stored token is expired, clearing session');
                clearAuthState();
              } else {
                setUser(userData);
                setIsAuthenticated(true);
                
                // Ensure login time is stored (for existing sessions)
                if (!localStorage.getItem('auth0_login_time')) {
                  localStorage.setItem('auth0_login_time', Date.now().toString());
                }
                
                // console.log('Restored valid Auth0 session:', userData.email);
              }
            } catch (err) {
              console.error('Error restoring session:', err);
              clearAuthState();
            }
          } else {
            // console.log('No stored session found');
          }
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError(err);
        setIsLoading(false);
      }
    };

    initAuth();
  }, [domain, clientId, onRedirectCallback]);

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      // JWT tokens have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      
      // Decode the payload (second part)
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Check if token is expired (exp claim is in seconds)
      return payload.exp && payload.exp < currentTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true; // If we can't parse it, consider it expired
    }
  };

  // Validate current session
  const validateSession = () => {
    const token = localStorage.getItem('auth0_access_token');
    const user = localStorage.getItem('auth0_user');
    
    if (!token || !user) {
      // console.log('No valid session found');
      return false;
    }
    
    if (isTokenExpired(token)) {
      // console.log('Token expired, clearing session');
      clearAuthState();
      return false;
    }
    
    return true;
  };

  const clearAuthState = () => {
    // console.log('Clearing Auth0 state...');
    
    // Clear all Auth0-related localStorage items
    localStorage.removeItem('auth0_access_token');
    localStorage.removeItem('auth0_id_token');
    localStorage.removeItem('auth0_user');
    localStorage.removeItem('auth0_login_time');
    localStorage.removeItem('auth0_state');
    localStorage.removeItem('auth0_nonce');
    
    // Clear any other potential Auth0 cache items
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('auth0')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear sessionStorage as well (Auth0 sometimes uses this)
    sessionStorage.removeItem('auth0_access_token');
    sessionStorage.removeItem('auth0_id_token');
    sessionStorage.removeItem('auth0_user');
    sessionStorage.removeItem('auth0_login_time');
    sessionStorage.removeItem('auth0_state');
    sessionStorage.removeItem('auth0_nonce');
    
    // Reset component state
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    
    // console.log('Auth0 state cleared completely');
  };

  const handleAuth0Error = (error, errorDescription) => {
    console.error('Auth0 error:', error, errorDescription);
    
    // Clear all Auth0 state on error to prevent retry loops
    clearAuthState();
    
    // Clear URL parameters to prevent automatic retries
    window.history.replaceState({}, document.title, window.location.pathname);
    
    // Set appropriate error message based on error type
    let errorMessage = 'Login failed. Please try again.';
    
    if (error === 'invalid_request') {
      if (errorDescription?.includes('organization membership')) {
        errorMessage = 'Your account is not associated with any organization. Please contact your administrator to be added to an organization.';
      } else if (errorDescription?.includes('organization')) {
        errorMessage = 'Organization error: ' + decodeURIComponent(errorDescription);
      } else {
        errorMessage = 'Invalid request: ' + (errorDescription ? decodeURIComponent(errorDescription) : 'Please try again.');
      }
    } else if (error === 'access_denied') {
      errorMessage = 'Access denied. Please check your credentials and try again.';
    } else if (error === 'unauthorized_client') {
      errorMessage = 'Unauthorized client. Please contact your administrator.';
    } else if (errorDescription) {
      errorMessage = decodeURIComponent(errorDescription);
    }
    
    setError(new Error(errorMessage));
    setIsAuthenticated(false);
    setUser(null);
    
    // Force a small delay to ensure state is properly cleared
    setTimeout(() => {
      // console.log('Auth0 error handling completed, ready for new login attempt');
    }, 100);
  };

  const loginWithRedirect = async (options = {}) => {
    try {
      // console.log('Redirecting to real Auth0 login...');
      
      // Clear any existing state before starting new login
      clearAuthState();

      // Use custom domain if available, otherwise fall back to default domain
      const authDomain = process.env.REACT_APP_AUTH0_CUSTOM_DOMAIN || domain;

      // Manual Auth0 login using Authorization Code flow
      const redirectUri = window.location.origin;
      const state = Math.random().toString(36).substring(2, 15);
      const nonce = Math.random().toString(36).substring(2, 15);
      
      // Add cache-busting parameter to prevent Auth0 from using cached credentials
      const cacheBuster = Date.now().toString();

      // Store state and nonce for validation - use both localStorage and sessionStorage for Firefox compatibility
      localStorage.setItem('auth0_state', state);
      localStorage.setItem('auth0_nonce', nonce);
      sessionStorage.setItem('auth0_state', state);
      sessionStorage.setItem('auth0_nonce', nonce);

      // Detect Firefox and add additional parameters for better compatibility
      const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      let authUrl = `https://${authDomain}/authorize?` +
        `response_type=code&` +
        `client_id=${clientId}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(authorizationParams.scope || 'openid profile email')}&` +
        `audience=${encodeURIComponent(authorizationParams.audience || '')}&` +
        `state=${state}&` +
        `nonce=${nonce}&` +
        `prompt=login&` + // Force fresh login, don't use cached credentials
        `_cb=${cacheBuster}`; // Cache buster parameter

      // Add Firefox-specific parameters for better compatibility
      if (isFirefox && isLocalhost) {
        authUrl += `&max_age=0&` + // Don't use cached authentication
                 `&ui_locales=en&` + // Force English locale
                 `&connection_scope=openid profile email`; // Explicit connection scope
      }

      // console.log('Redirecting to real Auth0:', authUrl);
      // console.log('Browser detection:', { isFirefox, isLocalhost });
      window.location.href = authUrl;
    } catch (err) {
      console.error('Login error:', err);
      setError(err);
      // Clear state on login error
      clearAuthState();
    }
  };

  const logout = async (options = {}) => {
    try {
      // console.log('Logging out from Auth0...');
      
      // Get token and user info BEFORE clearing
      const token = localStorage.getItem('auth0_access_token');
      const userStr = localStorage.getItem('auth0_user');
      let user = null;
      try {
        user = userStr ? JSON.parse(userStr) : null;
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
      
      // Call backend logout API with Authorization header BEFORE clearing tokens
      if (token) {
        try {
          const logoutPayload = {
            logoutReason: options.logoutReason || 'manual',
            timestamp: new Date().toISOString()
          };
          
          // Include user info as fallback if available
          if (user) {
            logoutPayload.userEmail = user.email;
            logoutPayload.userId = user.sub;
          }
          
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth0/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(logoutPayload)
          });
        } catch (apiError) {
          // Don't block logout if API call fails
          console.error('Logout API call failed:', apiError);
        }
      }
      
      // Clear local storage AFTER API call
      localStorage.removeItem('auth0_access_token');
      localStorage.removeItem('auth0_id_token');
      localStorage.removeItem('auth0_user');
      localStorage.removeItem('auth0_login_time');
      localStorage.removeItem('auth0_state');
      localStorage.removeItem('auth0_nonce');

      // Clear state
      setUser(null);
      setIsAuthenticated(false);

      // Redirect to login page
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      setError(err);
      
      // Even if there's an error, try to redirect to login
      window.location.href = '/';
    }
  };

  const getAccessTokenSilently = async (options = {}) => {
    try {
      // console.log('Getting real access token...');

      // Return stored token
      const storedToken = localStorage.getItem('auth0_access_token');
      if (storedToken) {
        // console.log('Real access token obtained from storage');
        return storedToken;
      } else {
        throw new Error('No access token available. Please log in.');
      }
    } catch (err) {
      console.error('Token error:', err);
      throw err;
    }
  };

  // Separate effect for browser close detection to avoid dependency issues
  useEffect(() => {
    // Store a timestamp when the page is about to unload
    const handleBeforeUnload = (event) => {
      // Don't clear session on page reload, only store a timestamp
      if (isAuthenticated && !isLoading) {
        // console.log('Page unloading, storing timestamp...');
        sessionStorage.setItem('page_unload_time', Date.now().toString());
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isAuthenticated) {
        // console.log('Page hidden, session will timeout on next activity check');
      }
    };
    
    // Handle page load to detect if this is a reload or a new session
    const handlePageLoad = () => {
      const unloadTime = sessionStorage.getItem('page_unload_time');
      
      if (unloadTime) {
        // If page was unloaded recently (within 2 seconds), this is a reload
        const timeSinceUnload = Date.now() - parseInt(unloadTime);
        if (timeSinceUnload < 2000) {
          // console.log('Page reload detected, preserving session');
          // Clear the unload time
          sessionStorage.removeItem('page_unload_time');
        } else {
          // If it's been more than 2 seconds, likely a new session or browser reopening
          // console.log('New session or browser reopening detected');
          sessionStorage.removeItem('page_unload_time');
        }
      } else {
        // No unload time means this is a fresh page load (not a reload)
        // console.log('Fresh page load detected');
      }
    };
    
    // Call handlePageLoad on component mount
    handlePageLoad();

    // Handle actual browser/tab close (not reload)
    const handlePageHide = (event) => {
      // Check if this is a real browser close (persisted=false) vs page cache (persisted=true)
      if (!event.persisted && isAuthenticated && !isLoading) {
        // console.log('Browser/tab actually closing, cleaning up session...');
        // Use sendBeacon for more reliable cleanup on page close
        if (navigator.sendBeacon) {
          // Get user info from localStorage before sending
          let user = null;
          try {
            const userStr = localStorage.getItem('auth0_user');
            user = userStr ? JSON.parse(userStr) : null;
          } catch (e) {
            console.error('Error parsing user from localStorage:', e);
          }
          
          const logoutData = JSON.stringify({
            logoutReason: 'browser_close',
            timestamp: new Date().toISOString(),
            // Include user info so backend can identify user (sendBeacon can't send Authorization header)
            ...(user?.email && { userEmail: user.email }),
            ...(user?.sub && { userId: user.sub })
          });
          navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/auth0/logout`, logoutData);
        }
        // We don't clear auth state here to avoid affecting page reloads
      }
    };

    // Add event listeners for browser/tab events
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    injectAuthToken(getAccessTokenSilently, validateSession, isTokenExpired);
  }, [getAccessTokenSilently, validateSession, isTokenExpired]);

  const contextValue = {
    isLoading,
    isAuthenticated,
    user,
    error,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    clearAuthState,
    handleAuth0Error,
    validateSession,
    isTokenExpired
  };

  return (
    <Auth0Context.Provider value={contextValue}>
      {children}
    </Auth0Context.Provider>
  );
};
